import React from 'react';
import SEO from './SEO';
import AppStoreBadge from './AppStoreBadge';
import Icon from './Icon';

const SavingsGoals = () => {
  const savingsGoalsStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Homeownership Savings Goals & Down Payment Assistance",
      "url": "https://www.lown.app/savings-goals",
      "description": "Set and track savings goals for eco-friendly homes, walkable neighborhoods, and down payment assistance programs with Lown's free, ad-free mortgage calculator app.",
      "inLanguage": "en-US",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.lown.app/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Savings Goals",
            "item": "https://www.lown.app/savings-goals"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Plan Homeownership Savings Goals",
      "description": "Plan down payments, emergency funds, and payoff strategies using FHA, USDA, and VA loan programs with Lown's free savings goal tracker.",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Set a down payment goal",
          "text": "Use Lown to calculate how much house you can afford and set savings goals for escrow, closing costs, and eco-friendly upgrades."
        },
        {
          "@type": "HowToStep",
          "name": "Explore financing programs",
          "text": "Compare FHA down payment assistance, USDA loan eligibility, and VA loan requirements to see which program fits your budget."
        },
        {
          "@type": "HowToStep",
          "name": "Track progress weekly",
          "text": "Log deposits and automate transfers to reach milestones for walkable neighborhoods or homes with home office space."
        }
      ],
      "tool": [
        {
          "@type": "HowToTool",
          "name": "Lown savings goal tracker"
        }
      ],
      "supply": [
        {
          "@type": "HowToSupply",
          "name": "Monthly savings contributions"
        }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Homeownership Savings Goals, Down Payment Assistance & Mortgage Payoff"
        description="Set savings goals for eco-friendly homes, walkable neighborhoods, and down payment assistance programs while tracking mortgage payoff progress with Lown's free, ad-free savings goal tracker."
        keywords="homeownership savings goals, FHA down payment assistance, USDA loan eligibility, VA loan requirements, eco-friendly homes near me, homes with home office space, walkable neighborhoods, mortgage payoff tracker, how much house can I afford savings plan"
        url="https://www.lown.app/savings-goals"
        type="article"
        image="https://www.lown.app/lown_homescreen.png"
        structuredData={savingsGoalsStructuredData}
      />
      
      <div className="savings-goals-page">
        <div className="page-header">
          <h1>Set Homeownership Savings Goals with Down Payment Assistance Insights</h1>
          <p className="page-subtitle">Turn saving into a game with free, ad-free tools while planning for FHA, USDA, or VA financing, eco-friendly upgrades, and homes with the space you need—all without sharing your data.</p>
        </div>

        <div className="gamification-features animate-fade-in-up">
          <div className="feature-grid">
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="target" />
              </div>
              <h3>Smart Goal Setting</h3>
              <p>Set realistic savings goals based on your income, timeline, and homeownership objectives. Get personalized recommendations for optimal savings targets.</p>
            </div>
            
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="chart" />
              </div>
              <h3>Progress Tracking</h3>
              <p>Visualize your savings journey with interactive charts, milestone celebrations, and detailed progress reports that keep you motivated.</p>
            </div>
            
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="trophy" />
              </div>
              <h3>Achievement System</h3>
              <p>Earn badges and rewards for reaching savings milestones, maintaining consistent deposits, and achieving financial goals.</p>
            </div>
            
            <div className="feature-card animate-fade-in-up">
              <div className="feature-icon app-icon">
                <Icon name="wallet" />
              </div>
              <h3>Mortgage Payoff Tracker</h3>
              <p>Track extra mortgage payments and see how they accelerate your payoff timeline while saving thousands in interest.</p>
            </div>
          </div>
        </div>

        <div className="coming-soon-section animate-fade-in-up">
          <div className="coming-soon-content">
            <h2>Coming Soon in the Lown App</h2>
            <p>Our gamified savings goal tracker is currently in development and will be available in the Lown app soon. This engaging feature will help you:</p>
            
            <div className="benefits-list">
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Set multiple savings goals (down payment, emergency fund, extra payments)</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Track daily, weekly, and monthly progress with visual charts</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Earn achievement badges for reaching milestones</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Get personalized savings tips and strategies</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Connect with your bank account for automatic tracking</span>
              </div>
              <div className="benefit-item">
                <span className="check-icon">✓</span>
                <span>Share achievements and progress with family and friends</span>
              </div>
            </div>
            
            <div className="cta-section animate-fade-in-up">
              <AppStoreBadge
                className="savings-app-store-cta"
                ariaLabel="Get the Lown app on the App Store"
              />
              <p className="cta-note">Be the first to experience gamified savings when it launches</p>
            </div>
          </div>
        </div>

        <div className="savings-strategies animate-fade-in-up">
          <h2>Smart Savings Strategies for Homeownership</h2>
          <div className="strategies-container">
            <div className="strategy-card animate-fade-in-up">
              <div className="strategy-icon app-icon">
                <Icon name="home" />
              </div>
              <h3>Down Payment Savings</h3>
              <p>Save 20% of your home's purchase price to avoid PMI and get better loan terms. Use our calculator to determine your target amount.</p>
              <ul>
                <li>Set up automatic transfers to savings</li>
                <li>Use high-yield savings accounts</li>
                <li>Consider down payment assistance programs</li>
              </ul>
            </div>
            
            <div className="strategy-card animate-fade-in-up">
              <div className="strategy-icon app-icon">
                <Icon name="shield" />
              </div>
              <h3>Emergency Fund</h3>
              <p>Build 3-6 months of expenses in emergency savings before buying a home. This protects you from unexpected costs.</p>
              <ul>
                <li>Start with $1,000 emergency fund</li>
                <li>Gradually build to 3-6 months expenses</li>
                <li>Keep in easily accessible account</li>
              </ul>
            </div>
            
            <div className="strategy-card animate-fade-in-up">
              <div className="strategy-icon app-icon">
                <Icon name="bolt" />
              </div>
              <h3>Extra Mortgage Payments</h3>
              <p>Make extra payments toward principal to pay off your mortgage faster and save thousands in interest.</p>
              <ul>
                <li>Make bi-weekly payments instead of monthly</li>
                <li>Add extra principal each month</li>
                <li>Use bonuses and tax refunds for extra payments</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="motivation-section animate-fade-in-up">
          <h2>Stay Motivated on Your Savings Journey</h2>
          <div className="motivation-tips">
            <div className="tip animate-fade-in-up">
              <div className="tip-icon app-icon">
                <Icon name="phone" />
              </div>
              <div className="tip-content">
                <h3>Track Daily Progress</h3>
                <p>Check your savings progress daily to stay engaged and motivated. Small daily actions lead to big results over time.</p>
              </div>
            </div>
            
            <div className="tip animate-fade-in-up">
              <div className="tip-icon app-icon">
                <Icon name="sparkle" />
              </div>
              <div className="tip-content">
                <h3>Celebrate Milestones</h3>
                <p>Reward yourself when you reach savings milestones. This positive reinforcement keeps you motivated for the long term.</p>
              </div>
            </div>
            
            <div className="tip animate-fade-in-up">
              <div className="tip-icon app-icon">
                <Icon name="users" />
              </div>
              <div className="tip-content">
                <h3>Share Your Goals</h3>
                <p>Tell family and friends about your savings goals. Their support and encouragement can help you stay accountable.</p>
              </div>
            </div>
            
            <div className="tip animate-fade-in-up">
              <div className="tip-icon app-icon">
                <Icon name="chart" />
              </div>
              <div className="tip-content">
                <h3>Visualize Your Progress</h3>
                <p>Use charts and graphs to see your savings grow over time. Visual progress is incredibly motivating and satisfying.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="faq-section animate-fade-in-up">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-container">
            <div className="faq-item">
              <h3>How much should I save for a down payment?</h3>
              <p>While 20% is ideal to avoid PMI, you can buy a home with as little as 3-5% down with certain loan programs. Use our affordability calculator to determine what works for your budget.</p>
            </div>
            
            <div className="faq-item">
              <h3>Should I prioritize saving for a down payment or paying off debt?</h3>
              <p>Generally, pay off high-interest debt first, then save for a down payment. However, the best strategy depends on your specific situation and interest rates.</p>
            </div>
            
            <div className="faq-item">
              <h3>How can I save money faster for a home?</h3>
              <p>Create a budget, reduce expenses, increase income through side hustles, automate savings, and consider high-yield savings accounts or CDs for better returns.</p>
            </div>
            
            <div className="faq-item">
              <h3>What's the best way to track my savings progress?</h3>
              <p>Use apps like Lown to set goals, track deposits, visualize progress, and stay motivated with gamification features and achievement systems.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SavingsGoals;
